import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Button } from '../../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card'
import { UserPlus, Users } from 'lucide-react'
import StudentDrawer from '../../leevo_ui/StudentDrawer/StudentDrawer'
import { hasDistinctStudentLegalName, studentDisplayName } from '../../../utilities/studentNames'

function StudentsListView({ students, facilityId, onAddStudent, onContinue, onStudentUpdated }) {
  const [editingStudent, setEditingStudent] = useState(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  function handleEditStudent(student) {
    setEditingStudent(student)
    setIsDrawerOpen(true)
  }

  function handleDrawerClose() {
    setIsDrawerOpen(false)
    setEditingStudent(null)
  }

  function handleStudentUpdated() {
    handleDrawerClose()
    if (onStudentUpdated) {
      onStudentUpdated()
    }
  }

  function handleStudentDeleted() {
    handleDrawerClose()
    if (onStudentUpdated) {
      onStudentUpdated()
    }
  }

  return (
    <>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Your Students</CardTitle>
            <CardDescription>Add students who will be registering for classes</CardDescription>
          </CardHeader>
          <CardContent>
            {students.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 mb-6">No students added yet</p>
                <Button onClick={onAddStudent} className="bg-purple-600 hover:bg-purple-700">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Your First Student
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {students.map((student) => (
                  <div
                    key={student.id}
                    className="flex justify-between items-center p-4 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors"
                  >
                    <div>
                      <h3 className="font-semibold text-gray-900">{studentDisplayName(student)}</h3>
                      {hasDistinctStudentLegalName(student) && (
                        <p className="text-sm text-gray-600">{student.fullName}</p>
                      )}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => handleEditStudent(student)}>
                      Edit
                    </Button>
                  </div>
                ))}
                <div className="flex gap-3 pt-4">
                  <Button onClick={onAddStudent} variant="outline" className="flex-1">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Another Student
                  </Button>
                  <Button
                    onClick={onContinue}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    disabled={students.length === 0}
                  >
                    Continue to Classes
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {editingStudent && (
        <StudentDrawer
          open={isDrawerOpen}
          onClose={handleDrawerClose}
          studentId={editingStudent.id}
          facilityId={facilityId}
          onUpdate={handleStudentUpdated}
          onDelete={handleStudentDeleted}
          allowLevelEditing={false}
        />
      )}
    </>
  )
}

StudentsListView.propTypes = {
  students: PropTypes.array.isRequired,
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onAddStudent: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
  onStudentUpdated: PropTypes.func
}

export default StudentsListView
